import { AfterContentChecked, Directive, EventEmitter, Input, OnDestroy, Optional, Output } from '@angular/core';
import { NgModel } from '@angular/forms';
import { BehaviorSubject, debounceTime, Subscription } from 'rxjs';

import { TrackedGroupDirective } from './tracked-group.directive';

@Directive({
  selector: '[trackedControl]'
})
export class TrackedControlDirective implements OnDestroy, AfterContentChecked {

  @Input() previousValue: any;
  @Input() compareWith: (a: any, b: any) => boolean;
  @Input() displayAs: (a: any) => string;

  @Output('discard') public readonly discardEmitter = new EventEmitter();

  initialValue: any;

  public hasChanges: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public hasDirtyChanges: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private setupDone = false;
  private recheckPending = false;
  private subscriptions: Subscription[] = [];
  private readonly debouceTime: number = 100;

  constructor(
    private model: NgModel,
    @Optional() private trackedGroup: TrackedGroupDirective
  ) {
    if (trackedGroup) {
      trackedGroup.addControl(this);
    }
  }

  ngAfterContentChecked(): void {
    if (this.model && !this.setupDone) {
      this.setupModel();
      this.setupDone = true;
    } else if (this.setupDone) {
      this.recheckPristine();
    }
  }

  /**
   * `valueChanges` no alcanza para mantener `hasChanges` al día: no se dispara si cambia
   * `previousValue` y el valor no, ni si llega el mismo valor primitivo en una negociación
   * recargada. Los controles deshabilitados (que el usuario no puede tocar) quedaban marcados
   * como modificados con un valor anterior idéntico al actual. Mientras el control esté
   * pristine se recompara contra `viewModel`, que NgModel actualiza en forma sincrónica.
   */
  private recheckPristine(): void {
    if (this.recheckPending || !this.model.pristine) return;

    const value = this.model.viewModel;
    const hasChanges = !this.equals(value, this.previousValue);

    if (hasChanges !== this.hasChanges.value) {
      this.recheckPending = true;
      // Fuera del ciclo de detección actual: negotiable-group puede ya haber sido chequeado.
      Promise.resolve().then(() => {
        this.recheckPending = false;
        if (!this.model.pristine) return;
        this.initialValue = value;
        this.hasChanges.next(hasChanges);
      });
    }
  }

  private setupModel(): void {
    this.checkValue(this.model.value);
    this.subscriptions.push(this.model.valueChanges.pipe(
      debounceTime(this.debouceTime)
    ).subscribe(value => {
      this.checkValue(value);
    }));
  }

  checkValue(value: any) {
    if (this.model.pristine) {
      // Update initial value
      this.initialValue = value;
      this.hasChanges.next(!this.equals(value, this.previousValue));
    } else {
      let equalsInitialValue = this.equals(value, this.initialValue);
      this.hasDirtyChanges.next(!equalsInitialValue);
      if (equalsInitialValue) {
        this.discard();
      }
    }
  }

  private equals(a, b): boolean {
    return this.compareWith ? this.compareWith(a, b) : a == b;
  }

  displayInitial(): string {
    return this.display(this.initialValue);
  }

  displayPrevious(): string {
    return this.display(this.previousValue);
  }

  private display(a): string {
    // false también se formatea (p. ej. selects Sí/No); si no, se mostraría "false" literal.
    return (a || a === false) && this.displayAs ? this.displayAs(a) : a;
  }

  discard(): void {
    this.model.reset(this.initialValue);
    this.discardEmitter.emit();
  }

  /** @ignore */
  ngOnDestroy(): void {
    // Unsubscribe from everything
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
